import { z } from 'zod';
/**
 * Contratos do atendimento (Etapa 3): o que o chat do cliente e a caixa de entrada
 * da equipe trocam com a API. O lado do cliente nunca recebe dado que ele não possa ver;
 * o lado da equipe recebe os campos do cliente já mascarados pelo perfil.
 */
// ─────────────────────────────── Lado do cliente ───────────────────────────────
export const iniciarAtendimentoSchema = z.object({
    /** Versão do aviso de privacidade que o cliente aceitou ao abrir o chat. */
    aceitePrivacidadeVersao: z.string().min(1).max(20),
    origem: z.enum(['web', 'app']).default('web'),
    /**
     * Lembrança do aparelho, guardada pelo navegador de uma visita anterior. Serve só para
     * o bot perguntar "é você, Fulano?" e poupar a digitação do documento.
     */
    dispositivoToken: z.string().max(200).optional(),
    /**
     * Para onde o técnico quer ir, quando há mais de um setor aberto a ele.
     *
     * Só o técnico usa: o cliente passa pela triagem, que é quem escolhe o setor dele. A API
     * confere se o setor pedido realmente atende técnico — pedir outro não leva a lugar
     * nenhum (06/10).
     */
    setorId: z.uuid().optional(),
});
/** Quanto tempo o aparelho lembra do cliente, renovado a cada uso (decisão de 24/09). */
export const DIAS_LEMBRAR_DISPOSITIVO = 90;
export const enviarMensagemSchema = z.object({
    texto: z.string().trim().min(1, 'Escreva uma mensagem').max(4000),
    /** Mensagem citada, quando a pessoa respondeu a uma anterior. */
    respondeAId: z.uuid().optional(),
});
export const prepararAnexoSchema = z.object({
    nome: z.string().trim().min(1).max(200),
    mimeType: z.string().trim().min(3).max(120),
    tamanho: z.coerce.number().int().positive(),
});
export const enviarAnexoSchema = z.object({
    chave: z.string().min(1).max(300),
    nome: z.string().trim().min(1).max(200),
    mimeType: z.string().trim().min(3).max(120),
    legenda: z.string().trim().max(1000).optional().or(z.literal('')),
});
// `TIPOS_ANEXO_ACEITOS` mora em `anexos.ts` desde 08/10, junto com a lista de extensões
// e as regras de o que cada formato faz na tela.
export const TAMANHO_MAXIMO_ANEXO = 20 * 1024 * 1024;
/**
 * Áudio que toca em qualquer aparelho, inclusive no Safari do iPhone.
 *
 * O que não está aqui — o webm/opus que o Firefox grava, o ogg — vira m4a no servidor.
 * Sem isso, um recado gravado no Firefox chega mudo para quem abriu o chat no celular.
 */
export const AUDIO_QUE_TOCA_EM_TUDO = ['audio/mp4', 'audio/aac', 'audio/mpeg'];
/** O mimeType pode vir com o codec junto (`audio/webm;codecs=opus`) — só o tipo importa. */
export function precisaConverterAudio(mimeType) {
    const tipo = mimeType.split(';')[0]?.trim().toLowerCase() ?? '';
    return tipo.startsWith('audio/') && !AUDIO_QUE_TOCA_EM_TUDO.includes(tipo);
}
export const avaliarAtendimentoSchema = z.object({
    nota: z.coerce.number().int().min(1).max(5),
    comentario: z.string().trim().max(500).optional().or(z.literal('')),
});
/**
 * O navegador deve voltar para esta conversa, ou abrir outra?
 *
 * Quem guarda o token da conversa é o aparelho, e ele não expira sozinho. Sem esta
 * pergunta, um atendimento **encerrado** era retomado para sempre: o cliente clicava em
 * "Iniciar atendimento", caía na tela de encerrado, voltava ao início e clicava de novo,
 * em círculo — foi o que aconteceu com um encerramento por inatividade, que não pede nota
 * e portanto nunca limpava o token.
 *
 * Encerrada só continua valendo enquanto houver nota a dar: aí a tela de avaliação precisa
 * reaparecer, e o servidor concorda (é ele quem segura o próximo atendimento por nota
 * pendente). Encerrada e quitada é história — o próximo clique abre protocolo novo.
 */
export function podeRetomar(estado) {
    if (estado.status !== 'encerrada')
        return true;
    return estado.pedirAvaliacao && estado.avaliacao === null;
}
/** O cliente diz que a lembrança do aparelho não é dele. */
export const esquecerDispositivoSchema = z.object({
    dispositivoToken: z.string().min(1).max(200),
});
// ───────────────────────── Vincular cadastro (lado da equipe) ─────────────────
/**
 * Quando a triagem não achou o cadastro, o atendente procura no ERP e vincula. Um dos
 * dois campos basta: o documento que o cliente ditou ou o código do cadastro no HubSoft.
 */
export const buscarCadastroSchema = z
    .object({
    documento: z.string().trim().max(20).optional().or(z.literal('')),
    hubsoftClienteId: z.coerce.number().int().positive().optional(),
})
    .refine((v) => !!v.documento || !!v.hubsoftClienteId, 'Informe o documento ou o código');
// ─────────────────────────────── Lado da equipe ────────────────────────────────
export const transferirConversaSchema = z
    .object({
    setorId: z.uuid().optional(),
    atendenteId: z.uuid().optional(),
    motivo: z.string().trim().max(300).optional().or(z.literal('')),
})
    .refine((v) => v.setorId || v.atendenteId, 'Escolha um setor ou um atendente');
export const encerrarConversaSchema = z.object({
    motivoTagId: z.uuid().optional(),
    /**
     * Nota que o atendente dá ao técnico de liberação. Opcional: não trava o encerramento —
     * num dia corrido, obrigar a avaliar só produziria nota dada no automático.
     */
    avaliacaoTecnico: z.coerce.number().int().min(1).max(5).optional(),
    avaliacaoTecnicoNota: z.string().trim().max(500).optional().or(z.literal('')),
    /** Mensagem de despedida enviada ao cliente antes de encerrar. */
    despedida: z.string().trim().max(1000).optional().or(z.literal('')),
    /**
     * Com mais de uma pessoa no atendimento, encerrar tem dois sentidos: `todos` fecha o
     * protocolo para o cliente; `apenas_eu` só me tira de dentro e os outros continuam.
     */
    escopo: z.enum(['todos', 'apenas_eu']).default('todos'),
});
/**
 * A despedida que o atendente envia ao encerrar, escrita por ele.
 *
 * Vazio apaga a dele e devolve a da casa — é como se desfaz a personalização sem precisar
 * de um botão "restaurar".
 */
export const despedidaFormSchema = z.object({
    texto: z.string().trim().max(1000),
});
/** O texto da casa, usado por quem nunca escreveu o seu. */
export const DESPEDIDA_PADRAO = 'Obrigado pelo contato! Precisando, é só chamar.';
export const notaInternaSchema = z.object({
    texto: z.string().trim().min(1).max(4000),
});
/** Recado da equipe no canal paralelo daquele atendimento. */
export const sussurroSchema = z.object({
    texto: z.string().trim().min(1).max(2000),
});
/**
 * Motivos de pausa que o atendente escolhe. Saber que alguém parou não basta para quem
 * supervisiona — almoço, reunião e banheiro pedem reações diferentes.
 *
 * **O motivo é também o status na conversa interna** (decisão de 08/10). Em vez de dois
 * controles com a palavra "Reunião" querendo dizer coisas diferentes, há um só: a pausa.
 * O que muda entre os motivos é se a pessoa continua alcançável por ligação.
 *
 * Banheiro e lanche são cinco minutos — e é justamente nesses cinco minutos que a
 * Liberação costuma precisar da pessoa. Reunião e treinamento são o contrário: a ligação
 * ali atrapalha outra coisa que também é trabalho.
 */
export const MOTIVOS_PAUSA = [
    { rotulo: 'Banheiro', barraLigacao: false },
    { rotulo: 'Lanche', barraLigacao: false },
    { rotulo: 'Almoço/Janta', barraLigacao: false },
    { rotulo: 'Reunião', barraLigacao: true },
    { rotulo: 'Alinhamento', barraLigacao: false },
    { rotulo: 'Treinamento', barraLigacao: true },
    { rotulo: 'Outro', barraLigacao: false },
];
/**
 * Este motivo de pausa impede ligação?
 *
 * Recebe o texto como está gravado, porque `motivo_pausa` é texto livre no banco — o que
 * não estiver na lista não barra nada. Barrar por engano uma pausa que ninguém reconhece
 * seria deixar a pessoa inalcançável sem ela saber por quê.
 */
export function pausaBarraLigacao(motivo) {
    if (!motivo)
        return false;
    return MOTIVOS_PAUSA.some((m) => m.rotulo === motivo && m.barraLigacao);
}
export const presencaSchema = z
    .object({
    status: z.enum(['online', 'pausa', 'offline']),
    /** Obrigatório ao entrar em pausa; ignorado nos outros estados. */
    motivo: z.string().trim().max(60).optional().or(z.literal('')),
})
    .refine((v) => v.status !== 'pausa' || !!v.motivo, {
    message: 'Escolha o motivo da pausa',
    path: ['motivo'],
});
/** Ligar e desligar a invisibilidade na conversa interna. */
export const invisivelSchema = z.object({ invisivel: z.boolean() });
/** Liga/desliga a fila automática de um setor, direto da tela de atendimento. */
export const distribuicaoSetorSchema = z.object({ automatica: z.boolean() });
// ─────────────────────────────── Tempo real (Socket.IO) ────────────────────────
/** Namespaces do Socket.IO. */
export const NS_CHAT_CLIENTE = '/cliente';
export const NS_CHAT_EQUIPE = '/equipe';
/** Eventos que o servidor empurra. O payload de `mensagem` é a própria MensagemChat. */
export const EVENTO = {
    /** Mensagem nova numa conversa (ambos os lados). */
    MENSAGEM: 'mensagem',
    /** Estado da conversa mudou: fila, atendente assumiu, encerrada… */
    CONVERSA: 'conversa',
    /** Alguém está digitando na conversa. */
    DIGITANDO: 'digitando',
    /** Mensagens foram lidas pelo outro lado. */
    LIDA: 'lida',
    /** A fila de um setor mudou (só equipe). */
    FILA: 'fila',
    /** Presença de um atendente mudou (só equipe). */
    PRESENCA: 'presenca',
    /** Recado no canal paralelo da equipe (só equipe). */
    SUSSURRO: 'sussurro',
    /**
     * O cliente falou num atendimento seu (só equipe).
     *
     * Existe separado de `MENSAGEM` porque chega pela sala pessoal, e não pela sala da
     * conversa: é o aviso de quem NÃO está com aquela conversa aberta. Carrega só o id —
     * quem está com ela aberta já recebeu a mensagem inteira pelo outro caminho, e mandar
     * o conteúdo duas vezes duplicaria a bolha na tela.
     */
    AVISO_MENSAGEM: 'aviso_mensagem',
};
/**
 * A inscrição de um aparelho no aviso de mensagem nova (Web Push).
 *
 * Os três campos vêm prontos do navegador (`PushSubscription`): o endpoint identifica o
 * aparelho no serviço de push, e as duas chaves cifram o conteúdo do aviso ponta a ponta —
 * nem o servidor do Google lê o que vai escrito.
 */
export const inscricaoPushSchema = z.object({
    endpoint: z.url().max(600),
    p256dh: z.string().min(1).max(200),
    auth: z.string().min(1).max(200),
});
export const cancelarPushSchema = z.object({ endpoint: z.url().max(600) });
//# sourceMappingURL=chat.js.map